import { Box } from "@outschool/backpack";
import {
  CurrentLearnerNeedsConsentQuery,
  CurrentLearnerNeedsConsentQueryVariables,
  SetCurrentLearnerConsentMutation,
  SetCurrentLearnerConsentMutationVariables,
} from "@outschool/gql-frontend-generated";
import { gql, useMutation, useQuery } from "@outschool/ui-apollo";
import { LearnerHealthConsentModal } from "@outschool/ui-components-shared";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useCurrentLearner } from "../providers/CurrentLearnerProvider";

const currentLearnerNeedsConsentQuery = gql`
  query CurrentLearnerNeedsConsent {
    currentLearnerNeedsConsent {
      needsHealthConsent
    }
  }
`;

const setCurrentLearnerConsentMutation = gql`
  mutation SetCurrentLearnerConsent($hasHealthConsent: Boolean) {
    setCurrentLearnerConsent(hasHealthConsent: $hasHealthConsent) {
      uid
    }
  }
`;

interface PostLoginLearnerConsentProps {
  onDone: () => void;
}

export function PostLoginLearnerConsent({
  onDone,
}: PostLoginLearnerConsentProps) {
  const currentLearner = useCurrentLearner();
  const learnerName = currentLearner?.name ?? "";

  const { data, loading } = useQuery<
    CurrentLearnerNeedsConsentQuery,
    CurrentLearnerNeedsConsentQueryVariables
  >(currentLearnerNeedsConsentQuery);
  const [setConsent] = useMutation<
    SetCurrentLearnerConsentMutation,
    SetCurrentLearnerConsentMutationVariables
  >(setCurrentLearnerConsentMutation);

  const needsHealthConsent =
    data?.currentLearnerNeedsConsent?.needsHealthConsent ?? false;

  const [isConsentFlowActive, setIsConsentFlowActive] = useState(false);
  const [isHealthModalOpen, setIsHealthModalOpen] = useState(false);

  const healthConsentRef = useRef<boolean | null>(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (loading || !data || hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    if (needsHealthConsent) {
      setIsConsentFlowActive(true);
      if (needsHealthConsent) {
        setIsHealthModalOpen(true);
      }
    } else {
      onDone();
    }
  }, [loading, data, needsHealthConsent, onDone]);

  const finishConsentFlow = useCallback(async () => {
    await setConsent({
      variables: {
        hasHealthConsent: healthConsentRef.current,
      },
    });
    setIsConsentFlowActive(false);
    onDone();
  }, [setConsent, onDone]);

  const handleHealthModalClose = useCallback(() => {
    setIsHealthModalOpen(false);
    finishConsentFlow();
  }, [finishConsentFlow]);

  if (loading || !data) {
    return null;
  }

  if (!needsHealthConsent) {
    return null;
  }

  return (
    <>
      {isConsentFlowActive && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1200,
          }}
        />
      )}

      <LearnerHealthConsentModal
        open={isHealthModalOpen}
        learnerName={learnerName}
        onSave={hasAgreed => {
          healthConsentRef.current = hasAgreed;
        }}
        onClose={handleHealthModalClose}
      />
    </>
  );
}
